import {
  Directive,
  Input,
  ElementRef,
  HostListener,
  ComponentRef,
  ViewContainerRef,
  OnDestroy,
  TemplateRef,
} from '@angular/core';
import {
  Overlay,
  OverlayRef,
  OverlayPositionBuilder,
  FlexibleConnectedPositionStrategy,
} from '@angular/cdk/overlay';
import { ComponentPortal } from '@angular/cdk/portal';
import { TooltipComponent } from './tooltip.component';

@Directive({
  selector: '[tooltip]',
  standalone: true,
})
export class TooltipDirective implements OnDestroy {
  @Input() tooltip: string | TemplateRef<unknown> = '';

  private overlayRef?: OverlayRef;
  private tooltipRef?: ComponentRef<TooltipComponent>;
  private positionStrategy?: FlexibleConnectedPositionStrategy;
  private hideTimeout?: ReturnType<typeof setTimeout>;
  private isMouseOverTooltip = false;

  constructor(
    private elementRef: ElementRef,
    private overlay: Overlay,
    private overlayPositionBuilder: OverlayPositionBuilder,
    private viewContainerRef: ViewContainerRef,
  ) {}

  @HostListener('mouseenter')
  show(): void {
    if (!this.hasContent()) {
      return;
    }

    // Cancelar cualquier ocultamiento pendiente
    if (this.hideTimeout) {
      clearTimeout(this.hideTimeout);
      this.hideTimeout = undefined;
    }

    // Si ya está visible, no hacer nada
    if (this.overlayRef && this.overlayRef.hasAttached()) {
      return;
    }

    // Crear overlay si no existe
    if (!this.overlayRef) {
      this.positionStrategy = this.overlayPositionBuilder
        .flexibleConnectedTo(this.elementRef)
        .withPositions([
          {
            originX: 'center',
            originY: 'bottom',
            overlayX: 'center',
            overlayY: 'top',
            offsetY: 8,
          },
          {
            originX: 'center',
            originY: 'top',
            overlayX: 'center',
            overlayY: 'bottom',
            offsetY: -8,
          },
        ]);

      this.overlayRef = this.overlay.create({
        positionStrategy: this.positionStrategy,
        scrollStrategy: this.overlay.scrollStrategies.close(),
      });
    }

    const tooltipPortal = new ComponentPortal(
      TooltipComponent,
      this.viewContainerRef,
    );
    this.tooltipRef = this.overlayRef.attach(tooltipPortal);
    this.tooltipRef.instance.content = this.tooltip;

    if (this.positionStrategy) {
      this.positionStrategy.positionChanges.subscribe((change) => {
        if (this.tooltipRef) {
          this.tooltipRef.instance.position =
            change.connectionPair.originY === 'bottom' ? 'bottom' : 'top';
          this.tooltipRef.changeDetectorRef.markForCheck();
        }
      });
    }

    this.setupTooltipEvents();
  }

  private hasContent(): boolean {
    if (!this.tooltip) return false;
    if (typeof this.tooltip === 'string') return this.tooltip.trim() !== '';
    return true;
  }

  private setupTooltipEvents(): void {
    if (!this.overlayRef) return;

    const overlayElement = this.overlayRef.overlayElement;

    overlayElement.addEventListener('mouseenter', () => {
      this.isMouseOverTooltip = true;
      if (this.hideTimeout) {
        clearTimeout(this.hideTimeout);
        this.hideTimeout = undefined;
      }
    });

    overlayElement.addEventListener('mouseleave', () => {
      this.isMouseOverTooltip = false;
      this.scheduleHide();
    });
  }

  @HostListener('mouseleave')
  hide(): void {
    this.scheduleHide();
  }

  private scheduleHide(): void {
    this.hideTimeout = setTimeout(() => {
      if (!this.isMouseOverTooltip && this.overlayRef) {
        this.overlayRef.dispose();
        this.overlayRef = undefined;
        this.positionStrategy = undefined;
        this.tooltipRef = undefined;
      }
    }, 100);
  }

  ngOnDestroy(): void {
    if (this.hideTimeout) {
      clearTimeout(this.hideTimeout);
    }
    if (this.overlayRef) {
      this.overlayRef.dispose();
    }
  }
}
